import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import dayjs from "dayjs";
import { DRIVERS_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import DriverFormDialog from "./DriverFormDialog";
import { useNavigate } from "react-router-dom";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import DriverSearchInput from "../../components/DriverSearchInput.js";
import { formatWithTzLabel } from "../../utils/dateUtils.js";
import { resolveSearchQueryForApi } from "../../utils/phoneSearch";

const columns = [
  { id: "code", label: "Driver Code" },
  { id: "name", label: "Name" },
  { id: "phone_no", label: "Phone No" },
  { id: "email", label: "Email" },
  {
    id: "date_of_birth",
    label: "Date of Birth",
    render: (d) =>
      d.date_of_birth ? dayjs(d.date_of_birth).format("DD/MM/YYYY") : "",
  },
  {
    id: "created_at",
    label: "Created On",
    render: (d) =>
      d.created_at ? formatWithTzLabel(d.created_at, "DD/MM/YYYY") : "",
  },
  {
    id: "updated_at",
    label: "Updated On",
    render: (d) =>
      d.updated_at ? formatWithTzLabel(d.updated_at, "DD/MM/YYYY") : "",
  },
  {
    id: "action",
    label: "Action",
    align: "center",
    render: (d, _, { handleEditClick }) => (
      <ButtonOne variant="contained" onClick={() => handleEditClick(d)}>
        Edit
      </ButtonOne>
    ),
  },
];

export default function UsersTable({ searchInput, updateSearchValue }) {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [driversData, setUsersData] = useState([]);
  const [driverModalState, setDriverModalState] = useState({
    isOpen: false,
    selectedClient: null,
    mode: "add",
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });

  useEffect(() => {
    const resolvedSearch = resolveSearchQueryForApi(searchInput);
    if (resolvedSearch.blockFetch) {
      setUsersData([]);
      setRowCount(0);
      return;
    }

    let searchURL = resolvedSearch.apiSearchQuery
    ? `&search_value=${encodeURIComponent(resolvedSearch.apiSearchQuery)}`
    : "";

    const fetchData = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-tenant-ID": tenant,
        };
        const response = await fetch(
          DRIVERS_ENDPOINT +
            `?limit=${controller.rowsPerPage}&offset=${controller.page * controller.rowsPerPage}` +
            searchURL,
          { headers },
        );
        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.code === "user_inactive") {
            auth.logOut();
            navigate("/login");
          }
          throw new Error("Failed to fetch data");
        }
        const jsonData = await response.json();
        setUsersData(jsonData.results);
        setRowCount(jsonData.count);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };
    fetchData();
  }, [controller, refreshKey, searchInput]);

  const handleEditClick = (driver) => {
    setDriverModalState({ isOpen: true, selectedClient: driver, mode: "edit" });
  };

  const handleAddClick = () => {
    setDriverModalState({ isOpen: true, selectedClient: null, mode: "add" });
  };

  const tableColumns = columns.map((col) =>
    col.id === "action"
      ? {
          ...col,
          render: (d) => (
            <ButtonOne variant="contained" onClick={() => handleEditClick(d)}>
              Edit
            </ButtonOne>
          ),
        }
      : col,
  );

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          py: 1,
          my: 1,
          bgcolor: "background.paper",
          borderRadius: 1,
        }}
      >
        <DriverSearchInput updateSearchValue={updateSearchValue} />
        <ButtonOne
          variant="contained"
          onClick={handleAddClick}
          sx={{ margin: 2 }}
        >
          Add Driver
        </ButtonOne>
      </Box>

      <AppTable
        columns={tableColumns}
        rows={driversData}
        rowKey={(d, i) => d.code ?? i}
        emptyMessage="No match found"
        maxHeight={440}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20]}
        onPageChange={(e, newPage) =>
          setController((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(e) =>
          setController((prev) => ({
            ...prev,
            rowsPerPage: parseInt(e.target.value, 20),
            page: 0,
          }))
        }
      />

      <DriverFormDialog
        modalState={driverModalState}
        handleModalState={setDriverModalState}
        selectedClient={driverModalState.selectedClient}
        refreshTable={setRefreshKey}
      />
    </Paper>
  );
}
